import { dateValue } from "./firestore";

// Port of LeagueDynamicFieldValue (Models/LeagueOrganizationAdminSchemaModel.swift).
// Firestore JS numbers carry no int/double distinction, so integral numbers
// decode as `int` — matching the iOS `value as? Int` behavior.

export type LeagueDynamicFieldValue =
  | { kind: "string"; value: string }
  | { kind: "int"; value: number }
  | { kind: "double"; value: number }
  | { kind: "bool"; value: boolean }
  | { kind: "date"; value: Date }
  | { kind: "stringList"; value: string[] };

export function decodeDynamicValue(value: unknown): LeagueDynamicFieldValue | null {
  if (typeof value === "boolean") {
    return { kind: "bool", value };
  }
  if (typeof value === "number") {
    return Number.isInteger(value) ? { kind: "int", value } : { kind: "double", value };
  }
  if (typeof value === "string") {
    return { kind: "string", value };
  }
  if (Array.isArray(value) && value.every((entry) => typeof entry === "string")) {
    return { kind: "stringList", value: value as string[] };
  }
  const date = dateValue(value);
  if (date) {
    return { kind: "date", value: date };
  }
  return null;
}

/**
 * Dates are returned as JS Dates: the Firestore client SDK stores them as
 * Timestamps, and JSON.stringify turns them into ISO strings for the Cloud
 * Function path (which converts them back server-side).
 */
export function encodeDynamicValue(value: LeagueDynamicFieldValue): unknown {
  return value.value;
}

export function decodeDynamicValues(map: unknown): Record<string, LeagueDynamicFieldValue> {
  if (!map || typeof map !== "object") return {};
  const result: Record<string, LeagueDynamicFieldValue> = {};
  for (const [key, raw] of Object.entries(map as Record<string, unknown>)) {
    const decoded = decodeDynamicValue(raw);
    if (decoded) result[key] = decoded;
  }
  return result;
}

export function encodeDynamicValues(values: Record<string, LeagueDynamicFieldValue>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(values)) {
    result[key] = encodeDynamicValue(value);
  }
  return result;
}

export function dynamicValueIsEmpty(value: LeagueDynamicFieldValue): boolean {
  switch (value.kind) {
    case "string":
      return value.value.trim().length === 0;
    case "stringList":
      return value.value.length === 0;
    default:
      return false;
  }
}

export function dynamicValueDisplayText(value: LeagueDynamicFieldValue | undefined): string {
  if (!value) return "—";
  switch (value.kind) {
    case "string":
      return value.value || "—";
    case "int":
    case "double":
      return String(value.value);
    case "bool":
      return value.value ? "Yes" : "No";
    case "date":
      return value.value.toLocaleString();
    case "stringList":
      return value.value.join(", ") || "—";
  }
}
